import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

/**
 * Technician Parts Ledger
 *
 * One row per dispatch (source: /technician/parts-ledger which reads
 * the part_dispatches collection). Shows complaint #, POD #, part code/name,
 * sent/received/used/returned/remaining quantities, dates and lifecycle status.
 */
const STATUS_CHIPS = {
  "Pending Receipt": "bg-amber-50 text-amber-700 border-amber-200",
  "Received": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Used": "bg-sky-50 text-sky-700 border-sky-200",
  "Returned": "bg-slate-100 text-slate-700 border-slate-300",
  "Partially Consumed": "bg-indigo-50 text-indigo-700 border-indigo-200",
};

export default function PartsLedger() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/technician/parts-ledger")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  const rows = (data?.rows || []).filter((r) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return [r.complaint_number, r.pod_number, r.part_name, r.part_code, r.status]
      .some((v) => (v || "").toString().toLowerCase().includes(needle));
  });
  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="parts-ledger">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Parts Ledger</h1>
        <p className="text-sm text-slate-500 mt-1">
          Every dispatch sent to you · sent / received / used / returned / remaining
        </p>
      </div>

      {/* Totals */}
      {data && (
        <Card>
          <CardContent className="p-3">
            <div className="grid grid-cols-5 gap-2 text-center text-xs">
              <KPI label="Sent" value={t.sent} colorClass="text-emerald-700" tid="kpi-sent" />
              <KPI label="Received" value={t.received} colorClass="text-sky-700" tid="kpi-received" />
              <KPI label="Used" value={t.used} colorClass="text-indigo-700" tid="kpi-used" />
              <KPI label="Returned" value={t.returned} colorClass="text-amber-700" tid="kpi-returned" />
              <KPI label="Remaining" value={t.remaining} colorClass="text-slate-900" tid="kpi-remaining" />
            </div>
          </CardContent>
        </Card>
      )}

      {err && <Card><CardContent className="p-4 text-rose-700 text-sm">{String(err)}</CardContent></Card>}
      {!data && !err && <div className="text-sm text-slate-500">Loading…</div>}

      {data && data.count === 0 && (
        <Card><CardContent className="p-6 text-center text-slate-400 text-sm">
          No parts dispatched to you yet. Once Spares dispatches parts to you, they appear here.
        </CardContent></Card>
      )}

      {data && data.count > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
              <div className="text-xs text-slate-600">{rows.length} of {data.count} rows</div>
              <div className="relative">
                <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
                <Input className="pl-7 h-8 max-w-xs text-sm" placeholder="Search complaint / POD / part…"
                       value={q} onChange={(e) => setQ(e.target.value)}
                       data-testid="parts-ledger-search" />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <Th>Complaint</Th>
                    <Th>POD</Th>
                    <Th>Part</Th>
                    <Th align="right">Sent</Th>
                    <Th align="right">Recd</Th>
                    <Th align="right">Used</Th>
                    <Th align="right">Ret</Th>
                    <Th align="right">Rem</Th>
                    <Th>Status</Th>
                    <Th>Sent</Th>
                    <Th>Recd</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.dispatch_id} data-testid={`pl-row-${r.dispatch_id}`}>
                      <td className="px-2 py-1.5 font-mono">
                        {r.complaint_id ? (
                          <Link className="text-indigo-700 hover:underline" to={`/complaints/${r.complaint_id}`}>
                            {r.complaint_number || "—"}
                          </Link>
                        ) : (r.complaint_number || "—")}
                      </td>
                      <td className="px-2 py-1.5 font-mono" data-testid={`pl-pod-${r.dispatch_id}`}>{r.pod_number || "—"}</td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium text-slate-800">{r.part_name || "—"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.part_code || "—"}</div>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono" data-testid={`pl-sent-${r.dispatch_id}`}>{r.qty_sent}</td>
                      <td className="px-2 py-1.5 text-right font-mono" data-testid={`pl-recd-${r.dispatch_id}`}>{r.qty_received}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{r.qty_used}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{r.qty_returned}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-semibold">{r.qty_remaining}</td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${STATUS_CHIPS[r.status] || "bg-slate-50 border-slate-200 text-slate-700"}`}
                              data-testid={`pl-status-${r.dispatch_id}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">{r.sent_date || "—"}</td>
                      <td className="px-2 py-1.5 text-slate-500">{r.received_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPI({ label, value, colorClass, tid }) {
  return (
    <div>
      <div className={`font-bold text-base ${colorClass}`} data-testid={tid}>{value ?? 0}</div>
      <div className="text-slate-500">{label}</div>
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-[10px] uppercase tracking-wide text-slate-600 font-semibold px-2 py-1.5`}>{children}</th>;
}
